/**
 * The offline-work and kidnapping fields of an entity on the wire (systems/offlineWork.ts, docs/systems/OFFLINE_WORK.md):
 * an offline body's job, rank and guard, the sack and who carries it, the blow under way, the motion, and a
 * red name for ชื่อฉาว. One small object, null for everyone else, compared by value in the snapshot's diff.
 */
import { isOfflineJobId, type OfflineJobId } from '../data/offlineWork';
import type { KidnapMotion } from '../systems/offlineWork';
import type { Entity } from '../world';

/** Only the fields set are present. */
export interface NetKidnap {
  job?: OfflineJobId;
  rank?: number;
  guardUntil?: number;
  sack?: true;
  carriedBy?: number;
  carrying?: number;
  blowTgt?: number;
  blowT?: number;
  motion?: KidnapMotion;
  infamous?: true;
}

const q = (v: number, step: number): number => Math.round(v * step) / step + 0;

/** An entity's kidnapping fields, or null when it has none. */
export function netKidnap(e: Entity): NetKidnap | null {
  const k: NetKidnap = {};
  if (e.offline) {
    k.job = e.offline.job;
    k.rank = e.offline.rank;
    if (e.offline.guardUntil > 0) k.guardUntil = q(e.offline.guardUntil, 10);
  }
  if (e.sack) k.sack = true;
  if (e.carriedBy != null) k.carriedBy = e.carriedBy;
  if (e.carrying != null) k.carrying = e.carrying;
  if (e.blow) {
    k.blowTgt = e.blow.tgtId;
    k.blowT = q(e.blow.t, 100);
  }
  if (e.kidnapMotion) k.motion = e.kidnapMotion;
  if (e.infamous) k.infamous = true;
  for (const _ in k) return k;
  return null;
}

/** Two values of the field are the same (by value: a new object each snapshot is not a change). */
export function sameKidnap(a: NetKidnap | null | undefined, b: NetKidnap | null | undefined): boolean {
  if (!a || !b) return !a === !b;
  const ka = Object.keys(a) as (keyof NetKidnap)[];
  return ka.length === Object.keys(b).length && ka.every((key) => a[key] === b[key]);
}

/** Writes the field onto a replica's entity (absent fields are cleared). */
export function applyKidnap(e: Entity, k: NetKidnap | null | undefined): void {
  e.offline =
    k?.job && isOfflineJobId(k.job) ? { job: k.job, rank: k.rank ?? 0, guardUntil: k.guardUntil ?? 0 } : null;
  e.sack = !!k?.sack;
  e.carriedBy = k?.carriedBy ?? null;
  e.carrying = k?.carrying ?? null;
  e.blow = k?.blowTgt !== undefined ? { tgtId: k.blowTgt, t: k.blowT ?? 0 } : null;
  e.kidnapMotion = k?.motion ?? null;
  e.infamous = !!k?.infamous;
}
